/**
 * Side Panel Store
 *
 * Unified Zustand store for managing the workspace side panel.
 * Supports different panel modes: preview (table/linked table) and create table.
 *
 * @module features/workspace/stores/useSidePanelStore
 */

import { create } from 'zustand';

import type { PreviewItem } from '../components/PreviewPanel/types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Panel mode types
 * - previewTable: Viewing a table's details
 * - previewLinkedTable: Viewing a linked table's details
 * - createTable: Creating a new table wizard
 */
export type SidePanelMode = 'previewTable' | 'previewLinkedTable' | 'createTable' | null;

/**
 * Side panel store state
 */
interface SidePanelState {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Current panel mode */
  mode: SidePanelMode;
  /** Preview item (when mode is 'previewTable' or 'previewLinkedTable') */
  previewItem: PreviewItem | null;
  /** Whether the panel is expanded (larger width for data view) */
  isExpanded: boolean;
}

/**
 * Side panel store actions
 */
interface SidePanelActions {
  /**
   * Open panel in preview mode
   */
  openPreview: (item: PreviewItem) => void;

  /**
   * Open panel in create table mode
   */
  openCreateTable: () => void;

  /**
   * Close the panel
   */
  close: () => void;

  /**
   * Toggle favorite on the preview item
   */
  togglePreviewFavorite: () => void;

  /**
   * Expand the panel to show item content
   */
  expand: () => void;

  /**
   * Collapse the panel back to preview mode
   */
  collapse: () => void;
}

/**
 * Combined store type
 */
type SidePanelStore = SidePanelState & SidePanelActions;

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: SidePanelState = {
  isOpen: false,
  mode: null,
  previewItem: null,
  isExpanded: false,
};

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * Side panel store
 *
 * Manages the unified side panel state for workspace.
 * Supports switching between preview and create table modes.
 */
export const useSidePanelStore = create<SidePanelStore>((set, get) => ({
  ...initialState,

  openPreview: (item) => {
    const mode = item.type === 'table' ? 'previewTable' : 'previewLinkedTable';
    set({ isOpen: true, mode, previewItem: item });
  },

  openCreateTable: () => {
    set({ isOpen: true, mode: 'createTable', previewItem: null });
  },

  close: () => {
    set({ isOpen: false, mode: null, previewItem: null, isExpanded: false });
  },

  togglePreviewFavorite: () => {
    const { previewItem } = get();
    if (previewItem) {
      set({ previewItem: { ...previewItem, isFavorite: !previewItem.isFavorite } });
    }
  },

  expand: () => {
    set({ isExpanded: true });
  },

  collapse: () => {
    set({ isExpanded: false });
  },
}));
